import axios from 'axios';

const CHECK_INTERVAL = 10_000;
const IDLE_TIMEOUT = 120_000;
const RELOAD_GUARD_KEY = 'app_version_reload';
const currentVersion = process.env.VUE_APP_BUILD_VERSION as string;

let started = false;
let interceptorRegistered = false;
let interval: ReturnType<typeof setInterval> | undefined;
let generation = 0;
let checking = false;
let lastActivity = 0;
let trackingActivity = false;
const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'click', 'keydown', 'input', 'change', 'scroll', 'wheel', 'touchmove', 'focusin'];
let reloadAttempted = false;
const requests = new Set<unknown>();

function recordActivity(): void {
    lastActivity = Date.now();
}

function startActivityTracking(): void {
    if (trackingActivity) {
        return;
    }
    trackingActivity = true;
    recordActivity();
    for (const event of ACTIVITY_EVENTS) {
        document.addEventListener(event, recordActivity, {capture: true, passive: true});
    }
}

function stopActivityTracking(): void {
    if (!trackingActivity) {
        return;
    }
    trackingActivity = false;
    for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, recordActivity, true);
    }
}

function reloadApp(version: string): void {
    if (requests.size > 0 || reloadAttempted) {
        return;
    }
    reloadAttempted = true;
    try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, version);
    } catch (e) {
    }
    window.location.reload();
}

function canReloadAutomatically(version: string): boolean {
    if (requests.size > 0 || Date.now() - lastActivity < IDLE_TIMEOUT) {
        return false;
    }
    try {
        return sessionStorage.getItem(RELOAD_GUARD_KEY) !== version;
    } catch (e) {
        return false;
    }
}

async function checkAppVersion(): Promise<void> {
    if (!started || checking) {
        return;
    }
    checking = true;
    const checkGeneration = generation;
    try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {cache: 'no-store'});
        if (!response.ok) {
            return;
        }
        const data = await response.json();
        if (!started || checkGeneration !== generation) {
            return;
        }
        if (typeof data?.version !== 'string' || data.version.length === 0) {
            return;
        }
        if (data.version === currentVersion) {
            stopActivityTracking();
            return;
        }
        startActivityTracking();
        if (canReloadAutomatically(data.version)) {
            reloadApp(data.version);
        }
    } catch (e) {
    } finally {
        checking = false;
    }
}

function startAppVersionWatcher(): void {
    if (!currentVersion || started) {
        return;
    }
    started = true;
    if (!interceptorRegistered) {
        interceptorRegistered = true;
        axios.interceptors.request.use(config => {
            const adapter = config.adapter ?? axios.defaults.adapter;
            if (adapter) {
                config.adapter = async requestConfig => {
                    requestConfig.adapter = adapter;
                    requests.add(requestConfig);
                    try {
                        return await adapter(requestConfig);
                    } finally {
                        requests.delete(requestConfig);
                    }
                };
            }
            return config;
        });
    }
    checkAppVersion();
    interval = setInterval(checkAppVersion, CHECK_INTERVAL);
}

export function setAppVersionWatcherEnabled(enabled: boolean): void {
    if (enabled) {
        startAppVersionWatcher();
        return;
    }
    if (!started) {
        return;
    }
    started = false;
    generation++;
    clearInterval(interval);
    interval = undefined;
    stopActivityTracking();
}
